import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Card, H3, H4, Body, BodySm } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { api } from '@/lib/api';
import type { SharedAnswer } from '@/types';

export function SharePage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [answer, setAnswer] = useState<SharedAnswer & { question?: string; answerText?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shareId) return;
    api.getSharedAnswer(shareId)
      .then((data) => { setAnswer(data as SharedAnswer & { question?: string; answerText?: string }); })
      .catch(() => { setError('This shared answer could not be found or has expired.'); })
      .finally(() => setLoading(false));
  }, [shareId]);

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <header className="bg-white border-b border-surface-200 px-6 py-4 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2">
          <NotLegalIcon name="message" className="w-5 h-5 text-primary-600" />
          <span className="font-bold text-surface-900">notlegal.chat</span>
        </Link>
        <div className="h-5 w-px bg-surface-200" />
        <H3>Shared Answer</H3>
      </header>

      <div className="flex-1 p-6 max-w-3xl mx-auto w-full">
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <BodySm className="text-amber-800">
            <strong>Disclaimer:</strong> This is not legal advice. It is AI-generated general information for entertainment purposes only. Consult a qualified attorney for actual legal matters.
          </BodySm>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <Card padding="lg" className="text-center">
            <H4 className="mb-2">Answer not found</H4>
            <Body className="text-surface-500 mb-4">{error}</Body>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Go to notlegal.chat
            </Link>
          </Card>
        )}

        {answer && !loading && (
          <div className="space-y-4">
            {answer.question && (
              <Card padding="md" className="bg-surface-100">
                <BodySm className="font-semibold text-surface-500 mb-1">Question</BodySm>
                <Body>{answer.question}</Body>
              </Card>
            )}
            <Card padding="md">
              <BodySm className="font-semibold text-surface-500 mb-2">Answer</BodySm>
              <div className="prose prose-sm max-w-none text-surface-700">
                <ReactMarkdown>{answer.answerText ?? ''}</ReactMarkdown>
              </div>
            </Card>
            {answer.expiresAt && (
              <BodySm className="text-center text-surface-400">
                This link expires on {new Date(answer.expiresAt * 1000).toLocaleDateString()}
              </BodySm>
            )}
            <div className="text-center">
              <Link to="/chat" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                Ask your own legal question →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
